import Controller from '@ember/controller';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type { Option } from '@mylighthouse/prism-ember/components/prism/select-input';
import type {
  CustomEmberTableColumn,
  CustomEmberTableRow,
} from '@mylighthouse/prism-ember/types/components/prism/table';
import dayjs from 'dayjs';
import GeneralTablesCellsTdTwoLine from 'lumen/components/general/tables/cells/td-two-line';
import CellActions from 'lumen/components/settings/users/tables/cells/cell-actions';
import CellProductAccess from 'lumen/components/settings/users/tables/cells/cell-product-access';
import CellRole from 'lumen/components/settings/users/tables/cells/cell-role';
import type SubscriptionUser from 'lumen/models/subscription-user';
import type {
  SubscriptionUserData,
  UsersModel,
} from 'lumen/routes/hotel/settings/users';
import type { AccessRight } from 'lumen/utils/constants/users-constants';

const COLUMNS = {
  NAME: 'name',
  ROLE: 'role',
  RATE_INSIGHT: 'rateInsight',
  BI_BENCHMARKING: 'biBenchmarking',
  LAST_ACTIVE: 'lastActive',
  ACTIONS: 'actions',
} as const;

type SortableColumn = typeof COLUMNS.NAME | typeof COLUMNS.ROLE;

export default class HotelSettingsUsersController extends Controller {
  declare model: UsersModel;
  queryParams = ['sortBy', 'sortDirection', 'page', 'pageSize'];

  @service declare alerting: Services['alerting'];
  @service declare errorTracking: Services['error-tracking'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare intl: Services['intl'];
  @service declare store: Services['store'];
  @service declare toast: Services['toast'];

  @tracked bulkRoleOverride: AccessRight | undefined;
  @tracked isInviteModalVisible = false;

  @tracked page = 1;
  @tracked pageSize = 100;
  @tracked rowSelection: CustomEmberTableRow[] = [];
  @tracked sortBy: SortableColumn = COLUMNS.NAME;
  @tracked sortDirection: 'asc' | 'desc' = 'asc';

  pageSizeOptions = [100, 250, 500, 1000];

  get bulkBiBenchmarking(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.BI_BENCHMARKING);
  }

  get bulkRateInsight(): { checked: boolean; indeterminate: boolean } {
    return this.computeBulkProductState(COLUMNS.RATE_INSIGHT);
  }

  get bulkRole(): { isIndeterminate: boolean; value: AccessRight | 'MIXED' } {
    if (this.bulkRoleOverride) {
      return { isIndeterminate: false, value: this.bulkRoleOverride };
    }

    const roles = this.rowSelection.map(
      (row) => row[COLUMNS.ROLE] as AccessRight,
    );
    const uniqueRoles = [...new Set(roles)];
    const firstRole = uniqueRoles[0];

    if (uniqueRoles.length === 1 && firstRole) {
      return { isIndeterminate: false, value: firstRole };
    }

    return { isIndeterminate: true, value: 'MIXED' };
  }

  get columns(): CustomEmberTableColumn[] {
    return [
      {
        name: this.intl.t('settings.users.column.name'),
        valuePath: COLUMNS.NAME,
        tdComponent: GeneralTablesCellsTdTwoLine,
        textAlign: 'left' as const,
        width: 260,
      },
      {
        name: this.intl.t('settings.users.column.user_role'),
        valuePath: COLUMNS.ROLE,
        tdComponent: CellRole,
        textAlign: 'left' as const,
        width: 200,
      },
      {
        name: this.intl.t('settings.users.column.rate_insight_market_insight'),
        valuePath: COLUMNS.RATE_INSIGHT,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.bi_benchmarking'),
        valuePath: COLUMNS.BI_BENCHMARKING,
        tdComponent: CellProductAccess,
        textAlign: 'left' as const,
        isSortable: false,
      },
      {
        name: this.intl.t('settings.users.column.last_active'),
        valuePath: COLUMNS.LAST_ACTIVE,
        textAlign: 'left' as const,
        isSortable: false,
        width: 160,
      },
      {
        name: '',
        valuePath: COLUMNS.ACTIONS,
        tdComponent: CellActions,
        textAlign: 'left' as const,
        isSortable: false,
        width: 40,
      },
    ];
  }

  get currentPageSizeOption(): { label: string; value: number } {
    return {
      value: this.pageSize,
      label: this.intl.t('general.page_no_of_rows', {
        noOfRows: this.intl.formatNumber(this.pageSize),
      }),
    };
  }

  get hasSelection(): boolean {
    return this.rowSelection.length > 0;
  }

  get isLoading(): boolean {
    return this.model.loadUsersTask.isRunning ?? false;
  }

  get rows(): CustomEmberTableRow[] {
    if (this.users.length === 0) {
      return [];
    }

    const isLastAdmin =
      this.users.filter((u) => u.accessRight === 'ADMIN' && !u.isLocked)
        .length <= 1;

    return this.users.map((user) => ({
      userId: user.id,
      [COLUMNS.NAME]: {
        primary: `${user.firstName} ${user.lastName}`,
        secondary: user.email,
      },
      [COLUMNS.ROLE]: user.accessRight,
      [COLUMNS.RATE_INSIGHT]: toProductAccessState(
        user.hasRateInsightAccess,
        user.isLocked,
      ),
      [COLUMNS.BI_BENCHMARKING]: toProductAccessState(
        user.hasBiBenchmarkingAccess,
        user.isLocked,
      ),
      [COLUMNS.LAST_ACTIVE]: user.lastActive
        ? dayjs(user.lastActive).format('DD MMM YYYY')
        : this.intl.t('general.missing'),
      [COLUMNS.ACTIONS]: '',
      isDisabled:
        user.isLocked ||
        (isLastAdmin && user.accessRight === 'ADMIN' && !user.isLocked),
      onDelete: () => this.deleteUser(user),
      disableCheckboxSelection: user.isLocked,
    }));
  }

  get showingText(): string {
    if (this.totalRecords === 0) {
      return '';
    }
    const startIndex = (this.page - 1) * this.pageSize + 1;
    const endIndex = Math.min(this.page * this.pageSize, this.totalRecords);

    return this.intl.t('general.page_showing', {
      currentPage: this.intl.formatNumber(startIndex),
      lastPage: this.intl.formatNumber(endIndex),
      totalRecords: this.intl.formatNumber(this.totalRecords),
    });
  }

  get sorts(): { isAscending: boolean; valuePath: SortableColumn }[] {
    return [
      {
        valuePath: this.sortBy,
        isAscending: this.sortDirection === 'asc',
      },
    ];
  }

  get totalPages(): number {
    return this.usersData?.meta.page_count ?? 0;
  }

  get totalRecords(): number {
    return this.usersData?.meta.count ?? 0;
  }

  get users(): SubscriptionUser[] {
    return [...this.store.peekAll<SubscriptionUser>('subscription-user')];
  }

  private get usersData(): SubscriptionUserData | undefined {
    return this.model.loadUsersTask.lastSuccessful?.value ?? undefined;
  }

  private computeBulkProductState(column: string): {
    checked: boolean;
    indeterminate: boolean;
  } {
    if (this.rowSelection.length === 0) {
      return { checked: false, indeterminate: false };
    }

    const states = this.rowSelection.map((row) => row[column] as string);
    const allChecked = states.every((state) => state === 'checked');
    const allUnchecked = states.every(
      (state) => state === 'unchecked' || state === 'unavailable',
    );

    if (allChecked) {
      return { checked: true, indeterminate: false };
    }
    if (allUnchecked) {
      return { checked: false, indeterminate: false };
    }

    return { checked: false, indeterminate: true };
  }

  async deleteUser(user: SubscriptionUser): Promise<void> {
    const hotelName =
      this.hotelAndMarketService.hotelOrMarket?.hotelinfo?.name ?? '';

    const confirmWasClicked = await this.alerting.showConfirmWarningAlert({
      dangerMode: true,
      title: this.intl.t('settings.users.delete.confirm_title'),
      text: this.intl.t('settings.users.delete.confirm_message', {
        hotelName,
      }),
    });

    if (!confirmWasClicked) {
      return;
    }

    try {
      await user.destroyRecord();

      this.toast.success(this.intl.t('settings.users.delete.success'), {
        showIcon: true,
      });
    } catch (error: unknown) {
      this.errorTracking.captureException(error);
      this.toast.error(this.intl.t('settings.users.delete.failure'), {
        showIcon: true,
      });
    }
  }

  @action
  cancelSelection(): void {
    this.rowSelection = [];
    this.bulkRoleOverride = undefined;
  }

  @action closeInviteModal(): void {
    this.isInviteModalVisible = false;
  }

  @action
  handleBulkRoleChange(option: Option): void {
    this.bulkRoleOverride = option.key as AccessRight;
  }

  @action
  onChangePageSize(pageSizeOption: { label: string; value: number }): void {
    this.pageSize = pageSizeOption.value;
    this.page = 1;
  }

  @action onUpdateSorts(
    sorts: { isAscending: boolean; valuePath: string }[],
  ): void {
    if (sorts[0]) {
      this.sortBy = sorts[0].valuePath as SortableColumn;
      this.sortDirection = sorts[0].isAscending ? 'asc' : 'desc';
    } else {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    }
  }

  @action openInviteModal(): void {
    this.isInviteModalVisible = true;
  }

  @action
  setPage(page: number): void {
    this.page = page;
  }

  @action
  setRowSelection(
    rowSelection: CustomEmberTableRow | CustomEmberTableRow[],
  ): void {
    this.bulkRoleOverride = undefined;
    this.rowSelection = Array.isArray(rowSelection)
      ? rowSelection
      : [rowSelection];
  }
}

function toProductAccessState(hasAccess: boolean, isLocked: boolean): string {
  if (!hasAccess) {
    return 'unavailable';
  }
  if (isLocked) {
    return 'readonly';
  }
  return 'checked';
}
